import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Progress, Segmented, Drawer,
} from 'antd';
import { TeamOutlined, CalendarOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import ResizableTitle from '../components/ResizableTitle';
import { formatTS } from '../utils/time';

import { selectFilter } from '../utils/selectFilter';
const STATUS_LABEL = {
  assigned:          { color: 'default', text: 'Draft' },
  in_progress:       { color: 'blue',    text: 'In Progress' },
  completed:         { color: 'green',   text: 'Completed' },
  rescore_requested: { color: 'purple',  text: 'Rescore Requested' },
  canceled:          { color: 'red',     text: 'Canceled' },
};

const { RangePicker } = DatePicker;

function scoreColor(pct) {
  if (pct == null) return '#8c8c8c';
  if (pct >= 80) return '#52c41a';
  if (pct >= 60) return '#faad14';
  return '#ff4d4f';
}

const METRIC_COLS = [
  {
    title: 'Qty Evals Pending',
    dataIndex: 'PendingCount',
    key: 'PendingCount',
    width: 145,
    align: 'center',
    sorter: (a, b) => a.PendingCount - b.PendingCount,
    render: (v) => v > 0 ? <Tag color="blue">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Rescore Requested',
    dataIndex: 'RescoreCount',
    key: 'RescoreCount',
    width: 170,
    align: 'center',
    sorter: (a, b) => a.RescoreCount - b.RescoreCount,
    render: (v) => v > 0 ? <Tag color="purple">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Qty Completed',
    dataIndex: 'CompletedCount',
    key: 'CompletedCount',
    width: 130,
    align: 'center',
    sorter: (a, b) => a.CompletedCount - b.CompletedCount,
    render: (v) => v > 0 ? <Tag color="green">{v}</Tag> : <span style={{ color: '#bfbfbf' }}>0</span>,
  },
  {
    title: 'Avg Completed Score',
    dataIndex: 'AvgScore',
    key: 'AvgScore',
    width: 200,
    sorter: (a, b) => (a.AvgScore ?? -1) - (b.AvgScore ?? -1),
    render: (pct) => {
      if (pct == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={pct}
            showInfo={false}
            strokeColor={scoreColor(pct)}
            size="small"
            style={{ flex: 1, minWidth: 60 }}
          />
          <Typography.Text strong style={{ color: scoreColor(pct), whiteSpace: 'nowrap' }}>
            {pct}%
          </Typography.Text>
        </div>
      );
    },
  },
  {
    title: 'Target (Gold)',
    dataIndex: 'GoalTargetPct',
    key: 'GoalTargetPct',
    width: 120,
    align: 'right',
    sorter: (a, b) => (a.GoalTargetPct ?? -1) - (b.GoalTargetPct ?? -1),
    render: (v) => v != null ? `${v}%` : <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  {
    title: 'Total Pts',
    dataIndex: 'TotalEarned',
    key: 'TotalEarned',
    width: 100,
    align: 'right',
    sorter: (a, b) => a.TotalEarned - b.TotalEarned,
    render: (v) => v ?? 0,
  },
  {
    title: 'Total Possible Pts',
    dataIndex: 'TotalMax',
    key: 'TotalMax',
    width: 150,
    align: 'right',
    sorter: (a, b) => a.TotalMax - b.TotalMax,
    render: (v) => v ?? 0,
  },
];

const DRILL_COLS = (navigate) => [
  {
    title: 'Status',
    dataIndex: 'Status',
    key: 'Status',
    width: 160,
    render: (s) => {
      const m = STATUS_LABEL[s] || { color: 'default', text: s };
      return <Tag color={m.color}>{m.text}</Tag>;
    },
  },
  {
    title: 'Call Date',
    dataIndex: 'CallDate',
    key: 'CallDate',
    width: 105,
    render: (v) => v ?? '—',
  },
  {
    title: 'Agent',
    dataIndex: 'AgentName',
    key: 'AgentName',
    width: 150,
  },
  {
    title: 'Eval Form',
    key: 'Form',
    width: 200,
    render: (_, r) => r.FormName ? `${r.FormName} v${r.FormVersion}` : '—',
  },
  {
    title: 'Score',
    dataIndex: 'Score',
    key: 'Score',
    width: 170,
    render: (pct) => {
      if (pct == null) return <span style={{ color: '#bfbfbf' }}>—</span>;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress percent={pct} showInfo={false} strokeColor={scoreColor(pct)}
            size="small" style={{ flex: 1, minWidth: 60 }} />
          <Typography.Text strong style={{ color: scoreColor(pct), whiteSpace: 'nowrap' }}>{pct}%</Typography.Text>
        </div>
      );
    },
  },
  {
    title: 'Pts / Max',
    key: 'Pts',
    width: 100,
    align: 'right',
    render: (_, r) => r.TotalEarned != null ? `${r.TotalEarned} / ${r.TotalMax}` : '—',
  },
  {
    title: 'Created',
    dataIndex: 'CreatedAt',
    key: 'CreatedAt',
    width: 140,
    render: (v) => formatTS(v),
  },
  {
    title: '',
    key: 'view',
    width: 60,
    render: (_, r) => (
      <Button size="small" type="link" onClick={(e) => { e.stopPropagation(); navigate(`/cases/${r.ID}`); }}>
        View
      </Button>
    ),
  },
];

export default function EvalEvaluatorStatsPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [forms, setForms]         = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [skills, setSkills]       = useState([]);
  const [viewMode, setViewMode]   = useState('evaluator');
  const [colWidths, setColWidths] = useState({});
  const [drillOpen, setDrillOpen]     = useState(false);
  const [drillTitle, setDrillTitle]   = useState('');
  const [drillRows, setDrillRows]     = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  useEffect(() => {
    api.get('/api/eval-forms/names')
      .then((r) => setForms(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
    api.get('/api/agent-stats/options')
      .then((r) => {
        setCampaigns(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID })));
        setSkills(r.data.skills.map((s) => ({ label: s, value: s })));
      })
      .catch(() => {});
  }, []);

  const fetchStats = useCallback(async () => {
    const values = form.getFieldsValue();
    setLoading(true);
    try {
      const params = { group_by: viewMode === 'evaluator' ? 'evaluator' : 'month' };
      if (values.callDateRange?.[0]) params.call_date_start = values.callDateRange[0].format('YYYY-MM-DD');
      if (values.callDateRange?.[1]) params.call_date_end   = values.callDateRange[1].format('YYYY-MM-DD');
      if (values.createdRange?.[0])  params.created_start   = values.createdRange[0].format('YYYY-MM-DD');
      if (values.createdRange?.[1])  params.created_end     = values.createdRange[1].format('YYYY-MM-DD');
      if (values.form_names?.length)  params.form_names      = values.form_names.join(',');
      if (values.campaigns?.length)  params.campaigns       = values.campaigns.join(',');
      if (values.skills?.length)     params.skills          = values.skills.join(',');

      const res = await api.get('/api/evaluator-stats', { params });
      setRows(res.data);
    } catch {
      // silently fail — table stays empty
    } finally {
      setLoading(false);
    }
  }, [form, viewMode]);

  // Default call date to current month on first load
  useEffect(() => {
    form.setFieldsValue({ callDateRange: [dayjs().startOf('month'), dayjs()] });
  }, []); // eslint-disable-line

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const openDrill = useCallback(async (record) => {
    const values = form.getFieldsValue();
    const label  = record.Month ? `${record.EvaluatorName} — ${record.Month}` : record.EvaluatorName;
    setDrillTitle(label);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillRows([]);
    try {
      const params = { evaluator_name: record.EvaluatorName };

      if (record.MonthKey) {
        const [y, m] = record.MonthKey.split('-').map(Number);
        params.start_date = record.MonthKey + '-01';
        params.end_date   = `${record.MonthKey}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
      } else {
        if (values.callDateRange?.[0]) params.start_date = values.callDateRange[0].format('YYYY-MM-DD');
        if (values.callDateRange?.[1]) params.end_date   = values.callDateRange[1].format('YYYY-MM-DD');
      }
      if (values.createdRange?.[0])  params.created_start = values.createdRange[0].format('YYYY-MM-DD');
      if (values.createdRange?.[1])  params.created_end   = values.createdRange[1].format('YYYY-MM-DD');
      if (values.form_names?.length)  params.form_names    = values.form_names.join(',');
      if (values.campaigns?.length)  params.campaigns     = values.campaigns.join(',');
      if (values.skills?.length)     params.skills        = values.skills.join(',');

      const res = await api.get('/api/evaluator-stats/drill', { params });
      setDrillRows(res.data);
    } catch {
      setDrillRows([]);
    } finally {
      setDrillLoading(false);
    }
  }, [form]);

  // ── Pivot table (month-over-month mode only) ────────────────────────────────
  const pivotMonths = viewMode === 'month'
    ? [...new Set(rows.map((r) => r.MonthKey))].sort()
    : [];

  const pivotMonthLabels = {};
  rows.forEach((r) => { if (r.MonthKey) pivotMonthLabels[r.MonthKey] = r.Month; });

  const pivotRows = viewMode === 'month' ? (() => {
    const byEval = {};
    rows.forEach((r) => {
      if (!byEval[r.EvaluatorName]) byEval[r.EvaluatorName] = {};
      byEval[r.EvaluatorName][r.MonthKey] = { count: r.CompletedCount, score: r.AvgScore };
    });
    return Object.keys(byEval).sort().map((name) => ({ key: name, EvaluatorName: name, ...byEval[name] }));
  })() : [];

  const pivotColumns = viewMode === 'month' ? [
    { title: 'Evaluator', dataIndex: 'EvaluatorName', key: 'EvaluatorName', fixed: 'left', width: 160 },
    ...pivotMonths.map((mk) => ({
      title: pivotMonthLabels[mk] || mk,
      key: mk,
      width: 130,
      align: 'center',
      render: (_, record) => {
        const cell = record[mk];
        if (!cell) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600 }}>{cell.count ?? 0}</div>
            <div style={{ fontSize: 11, color: cell.score != null ? scoreColor(cell.score) : '#bfbfbf' }}>
              {cell.score != null ? `${cell.score}%` : '—'}
            </div>
          </div>
        );
      },
    })),
  ] : [];

  const evaluatorCol = {
    title: 'Evaluator',
    dataIndex: 'EvaluatorName',
    key: 'EvaluatorName',
    sorter: (a, b) => (a.EvaluatorName ?? '').localeCompare(b.EvaluatorName ?? ''),
  };

  const monthCol = {
    title: 'Month',
    dataIndex: 'Month',
    key: 'Month',
    width: 140,
    sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? ''),
  };

  const columns = (viewMode === 'month'
    ? [evaluatorCol, monthCol, ...METRIC_COLS]
    : [evaluatorCol, ...METRIC_COLS]
  ).map(toResizable);

  const tableRowKey = viewMode === 'month'
    ? (record) => `${record.EvaluatorName}|${record.MonthKey}`
    : 'EvaluatorName';
  const paginationLabel = viewMode === 'month' ? 'rows' : 'evaluators';

  const tableSummary = () => {
    const totEarned  = rows.reduce((s, r) => s + (r.TotalEarned  ?? 0), 0);
    const totMax     = rows.reduce((s, r) => s + (r.TotalMax     ?? 0), 0);
    const totPending = rows.reduce((s, r) => s + (r.PendingCount ?? 0), 0);
    const totRescore = rows.reduce((s, r) => s + (r.RescoreCount ?? 0), 0);
    const totDone    = rows.reduce((s, r) => s + (r.CompletedCount ?? 0), 0);
    const avgPct     = totMax > 0 ? Math.round((totEarned / totMax) * 1000) / 10 : null;
    const boldCell   = { fontWeight: 600 };
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ background: '#fafafa' }}>
          <Table.Summary.Cell index={0} style={boldCell}>Total</Table.Summary.Cell>
          {viewMode === 'month' && <Table.Summary.Cell index={1} />}
          <Table.Summary.Cell index={viewMode === 'month' ? 2 : 1} align="center" style={boldCell}>
            {totPending > 0 ? totPending : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={viewMode === 'month' ? 3 : 2} align="center" style={boldCell}>
            {totRescore > 0 ? totRescore : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={viewMode === 'month' ? 4 : 3} align="center" style={boldCell}>
            {totDone > 0 ? totDone : <span style={{ color: '#bfbfbf' }}>0</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={viewMode === 'month' ? 5 : 4} style={boldCell}>
            {avgPct != null ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Progress percent={avgPct} showInfo={false} strokeColor={scoreColor(avgPct)}
                  size="small" style={{ flex: 1, minWidth: 60 }} />
                <Typography.Text strong style={{ color: scoreColor(avgPct), whiteSpace: 'nowrap' }}>
                  {avgPct}%
                </Typography.Text>
              </div>
            ) : <span style={{ color: '#bfbfbf' }}>—</span>}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={viewMode === 'month' ? 6 : 5} align="right" style={boldCell}>
            <span style={{ color: '#bfbfbf' }}>—</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={viewMode === 'month' ? 7 : 6} align="right" style={boldCell}>
            {totEarned}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={viewMode === 'month' ? 8 : 7} align="right" style={boldCell}>
            {totMax}
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const exportToExcel = () => {
    const exportRows = rows.map((r) => {
      const base = {
        'Evaluator':               r.EvaluatorName,
        'Qty Evals Pending':       r.PendingCount,
        'Qty Rescore Requested':   r.RescoreCount,
        'Qty Completed':           r.CompletedCount,
        'Avg Completed Score (%)': r.AvgScore ?? '',
        'Target (Gold) (%)':       r.GoalTargetPct ?? '',
        'Total Pts':               r.TotalEarned,
        'Total Possible Pts':      r.TotalMax,
      };
      if (viewMode === 'month') base['Month'] = r.Month;
      return base;
    });
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Evaluator Stats');
    XLSX.writeFile(wb, 'eval-evaluator-stats.xlsx');
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Evaluator Stats
        </Typography.Title>
        <Segmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { label: 'By Evaluator',     value: 'evaluator', icon: <TeamOutlined /> },
            { label: 'Month Over Month', value: 'month',     icon: <CalendarOutlined /> },
          ]}
        />
      </div>

      <Form
        form={form}
        layout="inline"
        onFinish={fetchStats}
        style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
      >
        <Form.Item name="callDateRange" label="Call Date">
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="createdRange" label="Eval Created On">
          <RangePicker style={{ width: 280 }} />
        </Form.Item>

        <Form.Item name="form_names" label="Eval Form">
          <Select
            mode="multiple"
            allowClear
            placeholder="All forms"
            options={forms}
            style={{ width: 280 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="campaigns" label="Campaign">
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All campaigns"
            options={campaigns}
            style={{ width: 260 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item name="skills" label="Skill">
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All skills"
            options={skills}
            style={{ width: 240 }}
            maxTagCount="responsive"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Filter
            </Button>
            <Button onClick={() => { form.resetFields(); fetchStats(); }}>
              Reset
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToExcel}
              disabled={rows.length === 0}
            >
              Export
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey={tableRowKey}
        dataSource={rows}
        columns={columns}
        components={{ header: { cell: ResizableTitle } }}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} ${paginationLabel}` }}
        scroll={{ x: 'max-content' }}
        summary={rows.length > 0 ? tableSummary : undefined}
        onRow={(record) => ({
          onClick: () => openDrill(record),
          style: { cursor: 'pointer' },
        })}
      />

      {viewMode === 'month' && pivotRows.length > 0 && (
        <>
          <Typography.Title level={5} style={{ marginTop: 32, marginBottom: 12 }}>
            Month Over Month Pivot
          </Typography.Title>
          <Table
            rowKey="EvaluatorName"
            dataSource={pivotRows}
            columns={pivotColumns}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            bordered
          />
        </>
      )}

      <Drawer
        title={drillTitle}
        open={drillOpen}
        onClose={() => setDrillOpen(false)}
        width={1000}
        destroyOnClose
      >
        <Table
          rowKey="ID"
          dataSource={drillRows}
          columns={DRILL_COLS(navigate)}
          loading={drillLoading}
          size="small"
          pagination={{ defaultPageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} evaluations` }}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => navigate(`/cases/${record.ID}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Drawer>
    </>
  );
}
